import SeoLandingPage from "../components/SeoLandingPage";

const page = {
  path: "/products",
  breadcrumbName: "Product Suite",
  serviceType: "AI Talent Product Suite",
  eyebrow: "Aptofy Product Suite",
  title: "Aptofy Product Suite | TalentOS, IntelliHire & AutoCert",
  description:
    "Explore the Aptofy Product Suite — TalentOS™ for talent operations, IntelliHire™ for AI hiring, and AutoCert™ for automated certification and assessments.",
  keywords:
    "Aptofy Product Suite, Aptofy TalentOS, Aptofy IntelliHire, Aptofy AutoCert, AI talent platform, AI hiring software, automated certification software, talent operating system",
  h1: "Aptofy Product Suite: TalentOS™, IntelliHire™, and AutoCert™",
  intro:
    "Three connected products for modern talent teams — run hiring operations, interview with AI, and certify skills at scale. Bias-free evaluation by AI. Contextual decisions by humans.",
  sections: [
    {
      heading: "What Is the Aptofy Product Suite?",
      paragraphs: [
        "The Aptofy Product Suite brings together talent operations, intelligent hiring, and automated certification in one AI-powered ecosystem. Teams can source and manage talent, run structured interviews, and issue trusted certificates without stitching together disconnected tools.",
        "Each product works on its own or as part of the full suite — so you can start with the workflow you need most and expand as hiring and learning programs grow.",
      ],
      bullets: [
        "Aptofy TalentOS™ — talent operating system",
        "Aptofy IntelliHire™ — AI intelligent hiring",
        "Aptofy AutoCert™ — automated certification",
        "Shared scoring, proctoring, and reports",
        "Built for startups, enterprises, and institutes",
        "Carbon-efficient digital talent workflows",
      ],
    },
    {
      heading: "Aptofy TalentOS™",
      paragraphs: [
        "TalentOS™ is Aptofy’s talent operating system. It helps recruiters post jobs, manage pipelines, run assessments, and move candidates from invite to shortlist with structured, role-ready reports.",
      ],
      bullets: [
        "End-to-end talent pipeline management",
        "Role-based assessments and scorecards",
        "Recruiter platform as a service",
        "Consistent evaluation across locations",
      ],
    },
    {
      heading: "Aptofy IntelliHire™",
      paragraphs: [
        "IntelliHire™ is Aptofy’s intelligent hiring product. An AI agent conducts structured interviews, scores responses in real time, and helps talent teams shortlist faster with less scheduling overhead.",
      ],
      bullets: [
        "AI agent interviewing",
        "Real-time candidate scoring",
        "Proctored, cheat-resistant sessions",
        "Bias-reduced hiring insights",
      ],
    },
    {
      heading: "Aptofy AutoCert™",
      paragraphs: [
        "AutoCert™ automates certification assessments for training institutes, academies, and enterprise L&D teams. Deliver secure exams, evaluate learners, and generate verifiable certificates without building exam infrastructure from scratch.",
      ],
      bullets: [
        "Proctored certification exams",
        "Automated evaluation and scoring",
        "Certificate generation and verification",
        "Training-as-a-service workflows",
      ],
    },
    {
      heading: "Why Teams Choose the Suite",
      paragraphs: [
        "Use one platform language for hiring, interviewing, and certification. Aptofy keeps evaluation consistent, reports actionable, and human decision-makers in control of final outcomes.",
      ],
      bullets: [
        "Faster time-to-hire and time-to-certify",
        "Lower operational cost vs manual screening",
        "Fairer, more consistent evaluations",
        "Scales from niche roles to bulk drives",
      ],
    },
  ],
  faqs: [
    {
      question: "What products are in the Aptofy Product Suite?",
      answer:
        "The Aptofy Product Suite includes Aptofy TalentOS™, Aptofy IntelliHire™, and Aptofy AutoCert™ — covering talent operations, AI hiring, and automated certification.",
    },
    {
      question: "Can we use only one Aptofy product?",
      answer:
        "Yes. Each product can be adopted independently. Many teams start with IntelliHire for interviews or AutoCert for exams, then add TalentOS as hiring operations grow.",
    },
    {
      question: "Does Aptofy replace human hiring decisions?",
      answer:
        "No. Aptofy automates evaluation so humans can make contextual final decisions with better data, less bias, and less admin work.",
    },
    {
      question: "How do we get started?",
      answer:
        "Request a demo or start a free trial. Our team can help you choose TalentOS, IntelliHire, AutoCert, or a combined rollout.",
    },
  ],
  ctaHeading: "See the Aptofy Product Suite in action",
  ctaText:
    "Request a demo or start a free trial to explore TalentOS™, IntelliHire™, and AutoCert™ for your hiring and certification programs.",
};

export default function ProductSuitePage() {
  return <SeoLandingPage page={page} />;
}
